/* eslint-disable max-len */
import { memo, ReactElement, useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';

import { addParamsToUrl } from 'app/utils';
import { Tour } from 'app/types';

import { getLastId } from 'app/helpers';
import { ShareTourModal } from 'app/components/common';
import type { Folder } from 'app/hooks/useFolders';
import { MoveToFolderMenu } from '../MoveToFolderMenu/MoveToFolderMenu';

interface Props {
  data: Tour;
  onSelect?: (tour: Tour) => void;
  priority?: boolean;
  /** When provided, shows a kebab → Move-to-folder menu on the card. */
  folders?: Folder[];
  currentFolderId?: string | null;
  onMoveTour?: (tourId: string, folderId: string | null) => void;
  onCreateFolder?: (name: string) => string;
}

const TourThumb = memo(
  ({
    preferredSrc,
    fallbackSrc,
    priority,
  }: {
    /** Try this first. When the request 404s we transparently swap to
     *  `fallbackSrc`. Lets the card prefer the canonical
     *  tours/{id}/thumbnail.jpg the user set via Manage Layers / the
     *  thumbnail picker, without a separate HEAD round-trip. */
    preferredSrc: string | null;
    fallbackSrc: string | null;
    priority: boolean;
  }) => {
    const [loaded, setLoaded] = useState(false);
    // null = nothing tried yet, 'preferred' = trying preferredSrc,
    // 'fallback' = preferred failed, on the legacy URL, 'gone' = both
    // failed → keep the gradient placeholder.
    const [stage, setStage] = useState<'preferred' | 'fallback' | 'gone'>(() =>
      preferredSrc ? 'preferred' : fallbackSrc ? 'fallback' : 'gone',
    );
    const currentSrc =
      stage === 'preferred'
        ? preferredSrc
        : stage === 'fallback'
        ? fallbackSrc
        : null;
    const showImage = stage !== 'gone' && !!currentSrc;

    return (
      <>
        <div className="absolute inset-0 bg-gradient-to-br from-black/5 to-black/15 flex items-center justify-center">
          <svg
            width="24"
            height="24"
            viewBox="0 0 48 48"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-black/25"
          >
            <rect x="6" y="8" width="36" height="32" rx="3" />
            <circle cx="16" cy="18" r="3" />
            <path d="M6 34l12-12 10 10 6-6 8 8" />
          </svg>
        </div>
        {showImage && (
          <img
            // Keyed so the swap to fallbackSrc dismounts the broken-image
            // attempt cleanly rather than reusing the same <img>.
            key={stage}
            src={currentSrc ?? undefined}
            alt=""
            width={112}
            height={112}
            loading={priority ? 'eager' : 'lazy'}
            decoding="async"
            draggable={false}
            className={classNames(
              'absolute inset-0 w-full h-full object-cover block transition-opacity duration-300',
              loaded ? 'opacity-100' : 'opacity-0',
            )}
            onLoad={() => setLoaded(true)}
            onError={() => {
              setLoaded(false);
              if (stage === 'preferred' && fallbackSrc) {
                setStage('fallback');
              } else {
                setStage('gone');
              }
            }}
          />
        )}
      </>
    );
  },
);
TourThumb.displayName = 'TourThumb';

const TourCardImpl = ({
  data,
  onSelect,
  priority = false,
  folders,
  currentFolderId = null,
  onMoveTour,
  onCreateFolder,
}: Props): ReactElement => {
  const { name, full_id, address, thumbnailUrl } = data;
  const [shareOpen, setShareOpen] = useState(false);

  // Prefer the canonical custom thumbnail (tours/{id}/thumbnail.jpg)
  // written by tour-thumbnail-api. The legacy `thumbnailUrl` value on the
  // tour Firestore doc is what the v1 monolith stamped at upload time
  // (often a pano thumbnail_512) — show that only if the custom file
  // doesn't exist, which is detected via the <img>'s onError.
  const STORAGE_BUCKET =
    process.env.REACT_APP_STORAGE_BUCKET || 'id3d-prod.appspot.com';
  const tourIdShort = useMemo(() => getLastId(full_id) ?? '', [full_id]);
  const customThumbUrl = tourIdShort
    ? `https://storage.googleapis.com/${STORAGE_BUCKET}/tours/${tourIdShort}/thumbnail.jpg`
    : null;

  const tourLink = useMemo(
    () =>
      addParamsToUrl(process.env.REACT_APP_IFRAME_URL, [
        { param: 'tour_id', value: getLastId(full_id) ?? 'X' },
        { param: 'debug', value: 'false' },
        { param: 'mode', value: 'orbit' },
      ]),
    [full_id],
  );

  const handleOpen = useCallback(() => {
    if (onSelect) {
      onSelect(data);
    } else {
      window.open(tourLink, '_blank', 'noreferrer');
    }
  }, [onSelect, data, tourLink]);

  const handleKey = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleOpen();
      }
    },
    [handleOpen],
  );

  const showAddress = address && address !== 'not set';
  const menuDeps =
    folders && onMoveTour && onCreateFolder
      ? { folders, onMoveTour, onCreateFolder }
      : null;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleOpen}
      onKeyDown={handleKey}
      className="group relative bg-white border border-black/10 rounded-[18px] shadow-[0_0_10px_rgba(0,0,0,0.06)] w-full flex items-center gap-5 px-5 py-4 cursor-pointer transition-all hover:shadow-[0_0_20px_rgba(0,0,0,0.10)] hover:-translate-y-px hover:z-30 focus-within:z-30 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-black/20"
    >
      <div className="relative w-20 h-20 rounded-xl overflow-hidden shrink-0 bg-black/5">
        <TourThumb
          preferredSrc={customThumbUrl}
          fallbackSrc={thumbnailUrl}
          priority={priority}
        />
      </div>
      <div className="flex flex-col min-w-0 flex-1">
        <div className="flex items-center gap-2 min-w-0">
          <h3 className="text-[15px] font-semibold text-[#151A30] truncate m-0">
            {name}
          </h3>
          {data.isCollaborator && (
            <span
              className="shrink-0 inline-flex items-center px-1.5 h-5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-black/[0.06] text-black/55"
              title="Shared with you"
            >
              Shared with you
            </span>
          )}
        </div>
        {showAddress && (
          <p className="text-[13px] text-black/50 truncate m-0 mt-0.5">
            {address}
          </p>
        )}
      </div>
      {menuDeps && (
        <MoveToFolderMenu
          folders={menuDeps.folders}
          currentFolderId={currentFolderId}
          onMove={(folderId) => menuDeps.onMoveTour(full_id, folderId)}
          onCreate={(folderName) => menuDeps.onCreateFolder(folderName)}
          onShare={() => setShareOpen(true)}
        />
      )}

      <ShareTourModal
        values={{ isOpened: shareOpen, close: () => setShareOpen(false) }}
        tour={data}
      />
    </div>
  );
};

export const TourCard = memo(TourCardImpl);
